import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Reveal from '../ui/Reveal';

const phases = [
  {
    num: "01",
    title: "Diagnosis",
    subtitle: "Finding the invisible constraints.",
    desc: "We do not guess. We map the reality of your current operations through workflow shadowing, team interviews, and a forensic founder time audit.",
    builds: ["Operational Audit", "Systems Inventory", "Bottleneck Mapping"]
  },
  {
    num: "02",
    title: "Architecture",
    subtitle: "Designing the logic of scale.",
    desc: "We build the structural blueprints required for autonomous execution. This removes the ambiguity of who does what, and how it gets approved.",
    builds: ["Weekly Rhythm Design", "Decision Rights Map", "Accountability Architecture"]
  },
  {
    num: "03",
    title: "Enablement",
    subtitle: "Making the system stick.",
    desc: "A system is useless if it is ignored. We run live implementation, map friction points, and guide the team through adoption while you begin to step back.",
    builds: ["Team Onboarding", "Live Runs", "Adoption Adjustments"]
  },
  {
    num: "04",
    title: "Handover",
    subtitle: "Supervised independence.",
    desc: "We monitor execution from a distance. We ensure handoffs happen without context loss and the business operates smoothly without pulling you back in.",
    builds: ["Supervised Independence", "Results Measurement", "Completion Report"]
  }
];

export default function Framework() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  return (
    <section id="framework" ref={containerRef} className="relative bg-brand-deep text-brand-cream h-[400vh]">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        
        {/* Progress Line */}
        <div className="absolute left-6 md:left-12 top-0 bottom-0 w-px bg-brand-cream/10 z-0">
          <motion.div 
            className="w-full bg-brand-terra origin-top"
            style={{ scaleY: scrollYProgress, height: '100%' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10 pl-16 md:pl-32">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl mb-16">The 90-Day Framework</h2>
          </Reveal>

          <div className="relative h-[400px]">
            {phases.map((phase, index) => {
              // Calculate opacity and y position based on scroll progress
              const start = index * 0.25;
              const end = (index + 1) * 0.25;
              const isActive = useTransform(scrollYProgress, 
                [Math.max(0, start - 0.1), start, end - 0.1, end], 
                [0, 1, 1, 0]
              );
              const yPos = useTransform(scrollYProgress,
                [Math.max(0, start - 0.1), start, end - 0.1, end],
                [50, 0, 0, -50]
              );

              return (
                <motion.div 
                  key={phase.num}
                  style={{ opacity: isActive, y: yPos }}
                  className="absolute inset-0 flex flex-col justify-center pointer-events-none"
                >
                  <div className="grid md:grid-cols-2 gap-12 items-start pointer-events-auto">
                    <div>
                      <span className="text-brand-terra font-serif text-6xl block mb-4">{phase.num}</span>
                      <h3 className="font-serif text-3xl md:text-4xl mb-2">{phase.title}</h3>
                      <p className="text-brand-sand text-lg mb-6">{phase.subtitle}</p>
                      <p className="text-brand-cream/80 font-light leading-relaxed text-lg">
                        {phase.desc}
                      </p>
                    </div>
                    <div className="bg-brand-olive/50 p-8 border border-brand-cream/10 backdrop-blur-sm">
                      <h4 className="text-sm tracking-widest uppercase text-brand-cream/50 mb-6">What gets built</h4>
                      <ul className="space-y-4">
                        {phase.builds.map((item, i) => (
                          <li key={i} className="flex items-center gap-3 text-lg font-light">
                            <div className="w-1.5 h-1.5 bg-brand-terra rounded-full" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}